import { Empty, Spin } from 'antd';
import NewImageGallery from 'components/UI/NewImageGallery';
import { offerTypeMap } from 'enums/offerTypeMap';
import ProductOffer from 'model/ProductOffer';
import { memo, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { ModeType } from 'screens/Products/utils/enums/ModeType.enum';
import { getModePagination } from 'services/config';
import ProductItem from '../ProductItem';
import {
  Content,
  ContentRow,
  HideContainer,
  LastRef,
  TextLoadMore,
} from './styles';

interface Props {
  products: ProductOffer[];
  mode: ModeType;
  productsRendered: boolean;
  showMoreItens: boolean;
  lastRef: RefObject<HTMLDivElement>;
  offerInserted(offerId: number, offerType: offerTypeMap): void;
  runInMobile: boolean;
  page: number;
  pageSize: number;
  pagesCount: number;
  onChangePagination(page: number, pageSize: number): void;
}

function ProductList({
  products,
  mode,
  lastRef,
  productsRendered,
  showMoreItens,
  offerInserted,
  runInMobile,
  page,
  pageSize,
  pagesCount,
  onChangePagination,
}: Props) {
  const openImage = useRef(() => null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [modePagination, setModePagination] = useState(() => {
    if (isMobile) {
      return false;
    }
    const modePaginationForDesktop = getModePagination();
    if (modePaginationForDesktop) {
      return modePaginationForDesktop === 'true';
    }
    return true;
  });

  const loadingFC = useMemo(
    () => (
      <TextLoadMore>
        <Spin spinning size="small" />
        <p>Carregando itens...</p>
      </TextLoadMore>
    ),
    [],
  );

  const paginationTotal = useMemo(
    () => pagesCount * pageSize,
    [pagesCount, pageSize],
  );

  const handleOpenImage = useCallback((product: ProductOffer) => {
    setGalleryImages(product.imagesFormatted);
    setTimeout(() => {
      openImage.current();
    });
  }, []);

  const handleChangePagination = useCallback(
    (pageChanged: number, pageSizeChanged: number) => {
      onChangePagination(pageChanged, pageSizeChanged);
      contentRef.current?.scrollIntoView();
    },
    [onChangePagination],
  );

  return (
    <>
      <HideContainer>
        {galleryImages.length > 0 && (
          <NewImageGallery
            images={galleryImages}
            currentImage={galleryImages[0]}
            alt="imagem do produto"
            maxHeight="150px"
            open={openImage}
          />
        )}
      </HideContainer>
      <ContentRow>
        <Content
          ref={contentRef}
          className={products?.length ? '' : 'empty-data'}
          listMode={mode}
        >
          {products.map((product) => (
            <ProductItem
              isBuyIntention
              isBuyIntentionMobile={isMobile}
              key={product.id}
              product={product}
              mode={mode}
              openImage={handleOpenImage}
              offerInserted={offerInserted}
              runInMobile={runInMobile}
            />
          ))}

          {productsRendered && !products.length && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          <LastRef
            ref={lastRef}
            hidden={!showMoreItens || !productsRendered || modePagination}
          >
            {loadingFC}
          </LastRef>
        </Content>
      </ContentRow>
    </>
  );
}

export default memo(ProductList);
